import React, { useState } from "react";
import { Copy, ExternalLink, Check } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const POLAND_EXAMS = [
    {
        id: "pl-lte",
        name: "LANGUAGECERT Test of English (LTE)",
        code: "C338E3",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-test-of-english-lte/lte-4skills",
    },
    {
        id: "pl-esol",
        name: "LANGUAGECERT ESOL",
        code: "D460A8",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-international-esol",
    },
    {
        id: "pl-academic",
        name: "LANGUAGECERT Academic",
        code: "D460A8",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-academic/academic",
    },
    {
        id: "pl-general",
        name: "LANGUAGECERT General",
        code: "D460A8",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-general/general",
    },
];

const PolandCard = ({ exam }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(exam.code);
        } catch (_e) {
            const ta = document.createElement("textarea");
            ta.value = exam.code;
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
            } catch (_err) {}
            document.body.removeChild(ta);
        }
        trackEvent("code_copy", {
            surface: "poland",
            exam_id: exam.id,
            code: exam.code,
        });
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
    };

    const handleBookingClick = () => {
        trackEvent("booking_click", {
            surface: "poland",
            exam_id: exam.id,
        });
    };

    return (
        <div
            data-testid={`poland-card-${exam.id}`}
            className="flex flex-col bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(27,42,71,0.15)] hover:border-[#1B2A47]"
        >
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#D32F2F]">
                Poland code
            </span>
            <h3 className="mt-3 text-lg font-semibold text-[#0A1128] font-[Outfit] leading-snug min-h-[52px]">
                {exam.name}
            </h3>

            <div className="mt-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#64748B] mb-2">
                    Your LanguageCert code
                </p>
                <div className="bg-[#F8F9FA] border border-dashed border-[#CBD5E1] rounded-lg py-4 px-4 text-center">
                    <span
                        data-testid={`poland-code-value-${exam.id}`}
                        className="font-mono-code text-2xl font-bold tracking-[0.22em] text-[#0F172A] uppercase"
                    >
                        {exam.code}
                    </span>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2.5">
                <button
                    type="button"
                    onClick={handleCopy}
                    data-testid={`poland-copy-button-${exam.id}`}
                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-xs font-medium transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                        copied
                            ? "bg-[#ECFDF5] text-[#166534] border border-[#86EFAC] focus-visible:ring-[#16A34A]"
                            : "bg-white text-[#1B2A47] border border-[#E2E8F0] hover:border-[#1B2A47] hover:bg-[#F8F9FA] focus-visible:ring-[#1B2A47]"
                    }`}
                >
                    {copied ? (
                        <>
                            <Check className="w-3.5 h-3.5" />
                            Copied
                        </>
                    ) : (
                        <>
                            <Copy className="w-3.5 h-3.5" />
                            Copy code
                        </>
                    )}
                </button>
                <a
                    href={exam.bookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleBookingClick}
                    data-testid={`poland-book-button-${exam.id}`}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-xs font-medium bg-[#D32F2F] hover:bg-[#B71C1C] text-white transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D32F2F] focus-visible:ring-offset-2"
                >
                    Book exam
                    <ExternalLink className="w-3.5 h-3.5" />
                </a>
            </div>
        </div>
    );
};

export const PolandSection = () => {
    return (
        <section
            id="poland-codes"
            data-testid="poland-section"
            className="bg-[#F8F9FA] border-t border-[#E2E8F0]"
        >
            <div className="max-w-6xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D32F2F]">
                        Poland only
                    </span>
                    <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A1128] font-[Outfit]">
                        Special LanguageCert codes for users in Poland
                    </h2>
                    <p className="mt-4 text-base text-[#475569] leading-relaxed">
                        If you are booking from Poland, use the code that
                        matches your exam type.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                    {POLAND_EXAMS.map((exam, i) => (
                        <div
                            key={exam.id}
                            className="fade-up"
                            style={{ animationDelay: `${i * 60}ms` }}
                        >
                            <PolandCard exam={exam} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default PolandSection;
